import { IPodcastEpisodeFields, isDocument } from '@estercare/ester-shared'
import { FC, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../theme/theme'
import { Grid } from './Grid'
import { PreviewGuard } from './PreviewGuard'
import { Title6, BodySmall } from './styles/Typography'
import { BlockItem } from './BlockItem'
import { PodcastButton } from './PodcastButton'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { OnClickButton } from './buttons/onclick/OnClickButton'
import { TextLink } from './TextLink'
import { useWindowWidth } from '../hooks/useWindowWidth'
import { isTablet } from '../utils/devices'
import { useTranslation } from 'react-i18next'
import { useSelectUserLanguage } from '../state/selectors'
import { SupportedLanguage } from '@estercare/ester-shared'

export interface Params {
  title: string
  podcasts: IPodcastEpisodeFields[]
  backgroundColor?: string
  showAsArchive?: boolean
  scrollYPosition?: number
  initialPodsShowCount?: number
}

export const PodcastList: FC<Params> = ({
  title,
  podcasts,
  backgroundColor,
  showAsArchive = false,
  scrollYPosition,
  initialPodsShowCount = 9,
}) => {
  const { t } = useTranslation()
  const width = useWindowWidth()
  const userLanguage = useSelectUserLanguage()
  const noSwedishUser = userLanguage !== SupportedLanguage.SWEDISH

  const defaultStartingCount = showAsArchive ? 9 : 3
  const initialPodsShownCount = initialPodsShowCount ?? defaultStartingCount
  const loadMorePodsCount = 18

  const [podcastIndex, setPodcastIndex] = useState<number>(initialPodsShownCount)
  const latestEpisodes = Array.isArray(podcasts)
    ? // Array.sort mutates the original array, so we need to make a copy, otherwise we'll risk breaking other components
      [...podcasts].sort((podCastA, podcastB) => (podcastB?.episodeNumber || 0) - (podCastA?.episodeNumber || 0))
    : []

  const displayedPodcast = latestEpisodes.slice(0, podcastIndex)
  const morePodcastsToShow = displayedPodcast.length < podcasts.length - 1

  return (
    <PodcastContainer $backgroundColor={backgroundColor}>
      {title && (
        <>
          <Title6>{title}</Title6>
          <SwedishOnlyMessage>{noSwedishUser ? t('common.content.onlyAvailableInSwedish') : ''}</SwedishOnlyMessage>
        </>
      )}

      <StyledGrid $columns={isTablet(width) ? 2 : 3}>
        {Array.isArray(displayedPodcast) &&
          displayedPodcast.map((podcast, index) => {
            const { title, episodeNumber, description, slug } = podcast
            const href = `/portal/resource-center/podcasts/${slug}${scrollYPosition ? `?y=${scrollYPosition}&podcast=${podcastIndex}` : ''}`
            const podSubtitle = isDocument(description)
              ? documentToPlainTextString(description)
              : t('common.content.missingDescription')
            if (!title) return <PreviewGuard key={index} />
            return (
              <BlockItem height={98} key={index}>
                <PodcastButton title={title} subtitle={podSubtitle} number={`${episodeNumber || 0}`} href={href} />
              </BlockItem>
            )
          })}
      </StyledGrid>
      {morePodcastsToShow && (
        <>
          {showAsArchive ? (
            <ButtonContainer>
              <OnClickButton
                text={t('common.content.morePodcasts')}
                secondary
                onClick={() => setPodcastIndex((prevValue) => prevValue + loadMorePodsCount)}
              />
            </ButtonContainer>
          ) : (
            <LinkContainer>
              <TextLink text={t('common.content.toKnowledgeBank')} href="/portal/resource-center" />
            </LinkContainer>
          )}
        </>
      )}
    </PodcastContainer>
  )
}

const StyledGrid = styled(Grid)`
  margin-top: ${theme.spacing.medium}px;
`
const PodcastContainer = styled.div<{ $backgroundColor?: string }>`
  display: flex;
  flex-direction: column;
  ${({ $backgroundColor }) => $backgroundColor && `background-color: ${$backgroundColor};`}
`
const LinkContainer = styled.div`
  display: flex;
  margin-top: ${theme.spacing.large}px;
  justify-content: flex-end;
`
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
  margin-bottom: ${theme.spacing.xxlarge}px;
`

const SwedishOnlyMessage = styled(BodySmall)`
  color: ${theme.color.grey5};
  font-style: italic;
  min-height: 20px;
`
